import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { getBlogPost } from "@/lib/api";
import { TID, CONSULTATION } from "@/constants/testIds";
import ConsultationForm from "@/components/ConsultationForm";

export default function BlogDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);
  useEffect(() => {
    setPost(null); setNotFound(false);
    getBlogPost(slug).then(setPost).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) return (
    <div className="section-pad pt-40 text-center">
      <h1 className="font-display text-forest text-3xl">Post not found</h1>
      <Link to="/blog" className="mt-4 inline-flex items-center gap-2 text-teal">
        <ArrowLeft size={16} /> Back to the journal
      </Link>
    </div>
  );

  if (!post) return <div className="section-pad pt-40 text-forest/50 text-center">Loading…</div>;

  return (
    <div data-testid={TID.blogDetail}>
      <section className="pt-32 pb-16 bg-offwhite">
        <div className="container-editorial px-5 md:px-10">
          <Link to="/blog" className="eyebrow text-teal inline-flex items-center gap-2 mb-8">
            <ArrowLeft size={14} /> The Journal
          </Link>
          <div className="max-w-3xl">
            <div className="eyebrow text-teal">{post.category} · {post.read_minutes} min read</div>
            <h1 className="font-display text-forest text-4xl md:text-6xl leading-[1.02] mt-4">
              {post.title}
            </h1>
            <p className="mt-6 text-forest/70 text-lg leading-relaxed">
              {post.excerpt}
            </p>
            <div className="mt-8 text-xs text-forest/50">
              By {post.author} · {new Date(post.published_at).toLocaleDateString("en-IN", { year: "numeric", month: "long", day: "numeric" })}
            </div>
          </div>
        </div>
      </section>

      <div className="relative h-[45vh] md:h-[60vh] overflow-hidden">
        <img src={post.cover_image} alt="" className="absolute inset-0 w-full h-full object-cover" />
      </div>

      <section className="section-pad bg-offwhite">
        <div className="container-editorial max-w-2xl px-5 md:px-0 mx-auto">
          <div className="prose-editorial space-y-6 text-forest/90 text-[17px] leading-[1.75] font-body">
            {post.body.split(/\n\n+/).map((p, i) => (
              <p key={i} className={i === 0 ? "dropcap font-display text-forest text-2xl md:text-[26px] leading-snug italic" : ""}>
                {p}
              </p>
            ))}
          </div>
        </div>
      </section>

      <section className="section-pad bg-cream">
        <div className="container-editorial grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5">
            <div className="eyebrow text-teal mb-4"><span className="editorial-rule" /> Online Consultation</div>
            <h2 className="font-display text-forest text-3xl md:text-4xl leading-tight">
              Read the essay.<br />Then book the {CONSULTATION.duration}-min call.
            </h2>
            <p className="mt-6 text-forest/70 leading-relaxed">
              Long-form is a start. A live consultation is where we adapt the
              details to your family, your budget and your timeline — added
              straight to your Google Calendar.
            </p>
          </div>
          <div className="md:col-span-6 md:col-start-7 bg-white rounded-lg p-6 md:p-8 border border-forest/10">
            <div className="eyebrow text-teal mb-1">Free · Google Meet</div>
            <h3 className="font-display text-forest text-2xl mb-4 leading-tight">Book your slot</h3>
            <ConsultationForm source="blog_detail" ctaLabel="Book my consultation" compact />
          </div>
        </div>
      </section>
    </div>
  );
}
